import { useEvidenceSourceOpeners } from '@/composition/hosts/use-evidence-answer-ports';
import { IS_INSET_DESIGN_ENABLED, useIsNangoIntegrationsEnabled } from '@/features/flags';
import { useIsWorkSourceCitationChipsEnabled } from '@/features/flags/work-source-citation-chips-flag';
import { desktopControlApprovalPort } from '@/features/rpa/desktop-control-approval';
import {
  ProjectConversationTimeline as ProjectConversationTimelineView,
  type ProjectItemRendererDeps,
  type ProjectItemRendererHooks,
} from '@heidi/agents';
import {
  buildViewerSourceFromSearchSource,
  openSearchBlockSource,
} from '@heidi/evidence';
import type { Source } from '@heidi/evidence/contracts';
import { useIsMobile } from '@heidi/utils';
import { type ComponentProps, useCallback } from 'react';

import { createProjectItemRenderer } from '../timeline/renderProjectItem';

/**
 * The Evidence source opener, bound once for the whole timeline: an Evidence
 * step's sources row opens a source the way Evidence's own list does, and
 * reports it on the same surface.
 */
const useOpenEvidenceSource = () => {
  const sourceOpeners = useEvidenceSourceOpeners();
  const isMobile = useIsMobile();
  return useCallback(
    (source: Source, citationNumber: number) =>
      openSearchBlockSource(
        {
          source,
          isMobile,
          isAuthenticatedUrl: sourceOpeners.isAuthenticatedUrl,
          citationNumber,
          surfaceBlock: 'collapsed_sources_summary',
          sessionId: null,
        },
        {
          // Evidence's rule for its references (`ReferenceCard`): a library
          // document's title is its filename, which often names the patient,
          // and collection names are free text, so neither is reported.
          trackSourceClick: (props) =>
            sourceOpeners.trackSourceClick({
              ...props,
              collections: undefined,
              ...(source.type === 'user_document' && { source_title: undefined }),
            }),
          openLibraryDocument: sourceOpeners.openLibraryDocument,
          openInViewer: (src) =>
            sourceOpeners.openInViewer(buildViewerSourceFromSearchSource(src)),
          openInNewTab: sourceOpeners.openInNewTab,
        }
      ),
    [sourceOpeners, isMobile]
  );
};

// What the Work item renderer reads from the app. Module scope, so each hook
// keeps one identity; the renderer calls them where it always read them.
const ITEM_RENDERER_HOOKS: ProjectItemRendererHooks = {
  useCreateItemRenderer: () => {
    const openEvidenceSource = useOpenEvidenceSource();
    return useCallback(
      (deps: ProjectItemRendererDeps) =>
        createProjectItemRenderer({ ...deps, openEvidenceSource }),
      [openEvidenceSource]
    );
  },
  useIsNangoIntegrationsEnabled: () => useIsNangoIntegrationsEnabled(),
  useIsWorkSourceCitationChipsEnabled: () => useIsWorkSourceCitationChipsEnabled(),
  desktopControl: desktopControlApprovalPort,
};

type ProjectConversationTimelineProps = Omit<
  ComponentProps<typeof ProjectConversationTimelineView>,
  'itemRendererHooks' | 'insetDesign'
>;

export const ProjectConversationTimeline = (props: ProjectConversationTimelineProps) => (
  <ProjectConversationTimelineView
    {...props}
    itemRendererHooks={ITEM_RENDERER_HOOKS}
    // Read only where the fades render, as before: the read reports a PostHog flag call.
    insetDesign={props.showScrollFades !== false && IS_INSET_DESIGN_ENABLED()}
  />
);
